import { Box, Center, Grid, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import {
  IoArrowBackOutline,
  IoArrowForwardOutline,
  IoCalendarClearOutline,
} from "react-icons/io5";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Africa/Windhoek");

const YEAR_PAGE_SIZE = 12;

const YearlyPicker = ({ styling, value = [], maxDate, onChange }) => {
  const maxYear = maxDate ? dayjs.unix(maxDate).year() : dayjs().year();
  const [selectedYear, setSelectedYear] = useState(dayjs().subtract(1, "year").year());
  const [pageStartYear, setPageStartYear] = useState(
    dayjs().subtract(1, "year").year() - 5
  );
  const [selectedDate, setSelectedDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(0);
  const calendarRef = useRef(null);

  const years = useMemo(
    () => Array.from({ length: YEAR_PAGE_SIZE }, (_, index) => pageStartYear + index),
    [pageStartYear]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const startDate =
      value != null && value.length && value[0].toString().length
        ? dayjs.unix(value[0])
        : dayjs().subtract(1, "year").startOf("year");
    const year = startDate.year();

    setSelectedYear(year);
    setPageStartYear(year - 5);
    setSelectedDate(String(year));
  }, [value]);

  const handlePageChange = (direction) => {
    const nextStartYear = pageStartYear + direction * YEAR_PAGE_SIZE;
    if (direction > 0 && nextStartYear > maxYear) return;
    setPageStartYear(nextStartYear);
  };

  const handleDateChange = (year) => {
    const startOfYearUnix = dayjs().year(year).startOf("year").unix();
    const endOfYearUnix = dayjs().year(year).endOf("year").unix();

    setSelectedYear(year);
    setSelectedDate(String(year));
    onChange([startOfYearUnix, endOfYearUnix]);
  };

  const calendarStyling = styling?.calendar
    ? {
        [styling.calendar.placement[0]]: styling.calendar.spacing[0],
        [styling.calendar.placement[1]]: styling.calendar.spacing[1],
      }
    : {};

  return (
    <Box
      className="averly-date-picker year-picker"
      w={styling?.width || "auto"}
      h={styling?.height || "auto"}
      cursor="pointer"
      zIndex="999999"
      position="relative"
    >
      <VStack gap={styling?.spacing || "0"} position="relative">
        <HStack gap="5px" onClick={() => !showCalendar && setShowCalendar(1)}>
          <Icon as={IoCalendarClearOutline} boxSize="16px" color="#171C1E" />
          <Text
            fontSize={styling?.fontSize || "14px"}
            h={styling?.height || "auto"}
            display="inline-block"
            overflow="hidden"
          >
            {selectedDate}
          </Text>
        </HStack>
        <Box
          ref={calendarRef}
          bg="#F6F6F9"
          w="294px"
          h="auto"
          position="absolute"
          p="9px 5px 5px"
          borderRadius="5px"
          display={showCalendar ? "block" : "none"}
          {...calendarStyling}
          onMouseDown={(event) => event.preventDefault()}
        >
          <VStack gap="2px">
            <HStack className="header" w="100%" justifyContent="space-between">
              <Center className="iconBackground" w="30px" h="30px" onClick={() => handlePageChange(-1)}>
                <Icon as={IoArrowBackOutline} className="icon" boxSize="16px" />
              </Center>
              <Text fontSize="14px" color="#000" textAlign="center">
                {pageStartYear} - {pageStartYear + YEAR_PAGE_SIZE - 1}
              </Text>
              <Center className="iconBackground" w="30px" h="30px" onClick={() => handlePageChange(1)}>
                <Icon as={IoArrowForwardOutline} className="icon" boxSize="16px" />
              </Center>
            </HStack>
            <Grid
              gridTemplateColumns="repeat(3, 1fr)"
              gap="5px"
              bg="#fff"
              w="100%"
              p="5px"
              borderRadius="3px"
              mt="10px"
            >
              {years.map((year) => {
                const maxDateStatus = year <= maxYear;
                const activeYearStatus = selectedYear === year;

                return (
                  <Center
                    key={year}
                    w="100%"
                    h="44px"
                    cursor={maxDateStatus ? "pointer" : "not-allowed"}
                    color={
                      !maxDateStatus
                        ? "#afafaf"
                        : activeYearStatus
                          ? "#fff"
                          : "#697387"
                    }
                    bg={
                      !maxDateStatus
                        ? "#d8d9da"
                        : activeYearStatus
                          ? "#344054"
                          : year === dayjs().year()
                            ? "#C3F0C2"
                            : "#EAECF0"
                    }
                    borderRadius="3px"
                    _hover={
                      !maxDateStatus
                        ? { bg: "#d8d9da", color: "#afafaf" }
                        : { bg: "#344054", color: "#fff" }
                    }
                    onClick={() => {
                      if (!maxDateStatus) return;
                      handleDateChange(year);
                      setTimeout(() => {
                        setShowCalendar(0);
                      }, 200);
                    }}
                  >
                    <Text fontSize="14px" color="inherit">
                      {year}
                    </Text>
                  </Center>
                );
              })}
            </Grid>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default YearlyPicker;
